import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Search, Edit, Trash2, Eye, Calendar, Grid3X3, Info } from 'lucide-react';
import { EditProjectForm } from './EditProjectForm';
import { useToast } from '@/hooks/use-toast';

interface ProjectForGallery {
  id: string;
  title: string;
  description: string;
  location: string;
  status: string;
  featured: boolean;
  created_at: string;
  project_images: { 
    id: string;
    image_url: string; 
    alt_text: string;
    caption: string;
    sort_order: number;
  }[];
  project_category_assignments: { 
    project_categories: { id: string; name: string; slug: string } 
  }[];
}

interface FeaturedProject {
  project_id: string;
}

export const GalleryProjectsManager = () => {
  const [projects, setProjects] = useState<ProjectForGallery[]>([]);
  const [featuredProjectIds, setFeaturedProjectIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState<ProjectForGallery | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Load all projects
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          *,
          project_images(id, image_url, alt_text, caption, sort_order),
          project_category_assignments(
            project_categories(id, name, slug)
          )
        `)
        .order('created_at', { ascending: false });

      if (projectsError) throw projectsError;

      // Load featured project IDs
      const { data: featuredData, error: featuredError } = await supabase
        .from('featured_projects')
        .select('project_id')
        .eq('is_active', true);

      if (featuredError) throw featuredError;

      setProjects(projectsData || []);
      setFeaturedProjectIds(featuredData?.map(fp => fp.project_id) || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load projects",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteProject = async (projectId: string) => {
    if (!confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Project deleted successfully",
      });

      loadData();
    } catch (error) {
      console.error('Error deleting project:', error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    }
  };

  const toggleProjectStatus = async (projectId: string, currentStatus: string) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';

    try {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Project ${newStatus === 'published' ? 'published to gallery' : 'hidden from gallery'}`,
      });

      loadData();
    } catch (error) {
      console.error('Error updating project status:', error);
      toast({
        title: "Error",
        description: "Failed to update project status",
        variant: "destructive",
      });
    }
  };

  // Filter out featured projects and apply search
  const galleryProjects = projects.filter(project => 
    !featuredProjectIds.includes(project.id) &&
    (project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     project.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     project.description?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const publishedGalleryProjects = galleryProjects.filter(p => p.status === 'published');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Info Banner */}
      <Card className="border-l-4 border-l-blue-500">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Grid3X3 className="h-5 w-5" />
            Gallery Projects ({publishedGalleryProjects.length})
          </CardTitle>
          <CardDescription className="text-sm">
            These projects appear in the main gallery section of your website. The {featuredProjectIds.length} homepage featured projects are automatically excluded from this gallery view.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center space-x-2">
            <Search className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            <Input
              placeholder="Search gallery projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-h-[48px]"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold">{galleryProjects.length}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Total Gallery</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-green-600">{publishedGalleryProjects.length}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Published</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-yellow-600">{galleryProjects.filter(p => p.status === 'draft').length}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Drafts</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="text-center">
              <div className="text-xl sm:text-2xl font-bold text-blue-600">{featuredProjectIds.length}</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Featured</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Projects List */}
      {galleryProjects.length === 0 ? (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
          {searchTerm 
            ? 'No gallery projects match your search.' 
            : featuredProjectIds.length > 0 
              ? `No additional gallery projects. All ${projects.length} projects are currently featured on the homepage.` 
              : 'No projects found. Create your first project!'
          }
          </AlertDescription>
        </Alert>
      ) : (
        <div className="grid gap-4 sm:gap-6">
          {galleryProjects.map((project) => (
            <Card key={project.id}>
              <CardHeader>
                <div className="flex flex-col sm:flex-row items-start justify-between gap-2 sm:gap-4">
                  <div className="space-y-2 flex-1 min-w-0">
                    <CardTitle className="text-base sm:text-lg break-words">{project.title}</CardTitle>
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2 text-xs sm:text-sm text-muted-foreground">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span>{new Date(project.created_at).toLocaleDateString()}</span>
                      </div>
                      {project.location && (
                        <>
                          <span className="hidden sm:inline">•</span>
                          <span>{project.location}</span>
                        </>
                      )}
                    </div>
                  </div>
                    <div className="flex items-center space-x-2 self-start">
                      <Badge variant={project.status === 'published' ? 'default' : 'secondary'} className="text-xs">
                        {project.status}
                      </Badge>
                    </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 sm:space-y-4">
                  {project.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {project.description}
                    </p>
                  )}

                  {/* Categories */}
                  {project.project_category_assignments.length > 0 && (
                    <div className="flex flex-wrap gap-1 sm:gap-2">
                      {project.project_category_assignments.map((assignment, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {assignment.project_categories.name}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Images preview */}
                  {project.project_images.length > 0 && (
                    <div className="flex space-x-2 overflow-x-auto pb-2">
                      {project.project_images.slice(0, 4).map((image, index) => (
                        <img
                          key={index}
                          src={image.image_url}
                          alt={image.alt_text || project.title}
                          className="h-12 w-12 sm:h-16 sm:w-16 rounded object-cover flex-shrink-0"
                        />
                      ))}
                      {project.project_images.length > 4 && (
                        <div className="h-12 w-12 sm:h-16 sm:w-16 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">
                          +{project.project_images.length - 4}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:justify-between sm:space-y-0 pt-2 border-t">
                    <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-2">
                      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                        <DialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedProject(project)}
                            className="min-h-[44px] justify-center"
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[95vw] sm:max-w-4xl max-h-[90vh] overflow-y-auto m-2 sm:m-6">
                          <DialogHeader>
                            <DialogTitle>Edit Gallery Project</DialogTitle>
                          </DialogHeader>
                          {selectedProject && (
                            <EditProjectForm
                              project={{
                                ...selectedProject,
                                status: selectedProject.status as 'draft' | 'published',
                                project_images: selectedProject.project_images.map(img => ({
                                  id: img.id,
                                  image_url: img.image_url,
                                  alt_text: img.alt_text,
                                  caption: img.caption,
                                  sort_order: img.sort_order
                                })),
                                project_category_assignments: selectedProject.project_category_assignments.map(assignment => ({
                                  project_categories: { 
                                    id: assignment.project_categories.id,
                                    name: assignment.project_categories.name 
                                  }
                                }))
                              }}
                              onSuccess={() => {
                                setIsEditDialogOpen(false);
                                loadData();
                              }}
                            />
                          )}
                        </DialogContent>
                      </Dialog>

                      <Button
                        size="sm"
                        variant={project.status === 'published' ? 'secondary' : 'default'}
                        onClick={() => toggleProjectStatus(project.id, project.status)}
                        className="min-h-[44px] justify-center"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">
                          {project.status === 'published' ? 'Hide from Gallery' : 'Show in Gallery'}
                        </span>
                        <span className="sm:hidden">
                          {project.status === 'published' ? 'Hide' : 'Show'}
                        </span>
                      </Button>
                    </div>

                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => deleteProject(project.id)}
                      className="min-h-[44px] justify-center"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};